import React from 'react';
import styled from 'styled-components';
import { TreeNode } from '@hud/web-components/Charts/Treemap/Treemap';
import { smBold, smRegular } from '@hud/web-components/theme/typography';
import { themeColor } from '@hud/web-components/theme/colors';
import { formatMetricValue } from '../../utils';
import { formatDateTime } from '@hud/web-components/utils';
import { NodeData, isFileNodeData } from './Treemap';

export function TreemapTooltip<T extends NodeData>({
  leaf,
}: {
  leaf: TreeNode<T>;
}) {
  if (!leaf.data) return <Container>{leaf.name}</Container>;

  const data = leaf.data;

  if (isFileNodeData(data)) {
    return (
      <Container>
        <Title>{data.filePath}</Title>
        <Group>
          <Key>Functions mapped:</Key>
          <Value>{data.mappedFunctions}</Value>
        </Group>
        <Group>
          <Key>Lines of code:</Key>
          <Value>{data.linesOfCode}</Value>
        </Group>
        <Group>
          <Key>Last changed:</Key>
          <Value>
            {data.lastChangeAt ? formatDateTime(data.lastChangeAt, true) : '-'}
          </Value>
        </Group>
      </Container>
    );
  }

  return (
    <Container>
      <Title>{data.signature}</Title>
      <Group>
        <Key>File:</Key>
        <Value>{data.filePath}</Value>
      </Group>
      <Group>
        <Key>Lines of code:</Key>
        <Value>{data.linesOfCode}</Value>
      </Group>
      <Group>
        <Key>Runs/d:</Key>
        <Value>
          {formatMetricValue({
            value: data.normalizedRuns,
            format: 'number',
          })}
        </Value>
      </Group>
      <Group>
        <Key>Last changed:</Key>
        <Value>
          {data.lastChangeAt ? formatDateTime(data.lastChangeAt, true) : '-'}
        </Value>
      </Group>
      {data.totalEndpoints > 0 ? (
        <Group>
          <Key>Endpoints:</Key>
          <Value>
            {data.endpoints.map((e) => (
              <div>
                {e.method.toUpperCase()} {e.path}
              </div>
            ))}
          </Value>
        </Group>
      ) : null}
    </Container>
  );
}

const Container = styled.div`
  ${smBold};
  padding: 12px;
  color: ${themeColor('white')};
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const Title = styled.div`
  ${smBold};
  color: ${themeColor('white')};
`;

const Group = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 10px;
`;

const Key = styled.div`
  color: ${themeColor('textTertiary')};
  ${smRegular}
`;

const Value = styled.div`
  color: ${themeColor('white')};
  ${smRegular}
`;
